import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/lib/hooks/api/utils";

export function useCommunications(params?: {
  page?: number;
  limit?: number;
  search?: string;
  contact_id?: string;
  type_key?: string;
  direction?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: ["communications", params],
    queryFn: async () => {
      const supabase = getSupabase();
      let query = supabase
        .from("communications")
        .select("*, contacts:contact_id(id,name,company_name,email), type:type_id(id,key,label)");

      if (params?.contact_id) query = query.eq("contact_id", params.contact_id);
      if (params?.direction) query = query.eq("direction", params.direction);
      if (params?.status) query = query.eq("status", params.status);
      if (params?.search)
        query = query.or(`title.ilike.%${params.search}%,body.ilike.%${params.search}%`);

      if (params?.type_key) {
        const { data: types } = await supabase
          .from("communication_types")
          .select("id,key")
          .eq("key", params.type_key)
          .limit(1)
          .maybeSingle();
        if (types?.id) query = query.eq("type_id", types.id);
      }

      if (params?.limit) {
        const offset = (params.page || 0) * params.limit;
        query = query.range(offset, offset + params.limit - 1);
      }

      const { data, error } = await query.order("occurred_at", { ascending: false });
      if (error) {
        console.error("Error fetching communications:", error);
        throw new Error(error.message);
      }
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCommunication(id: string) {
  return useQuery({
    queryKey: ["communications", id],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("communications")
        .select("*, contacts:contact_id(id,name,company_name,email), type:type_id(id,key,label)")
        .eq("id", id)
        .single();

      if (error) {
        console.error("Error fetching communication:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCommunicationTypes() {
  return useQuery({
    queryKey: ["communication-types"],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("communication_types")
        .select("*")
        .eq("is_active", true)
        .order("label", { ascending: true });
      if (error) throw new Error(error.message);
      return data || [];
    },
    staleTime: 10 * 60 * 1000,
  });
}
